import { useEffect, useState } from "react";
import { Download, Eye, EyeOff, ImagePlus, Images, Loader2, Pencil, Plus, Tags, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImage } from "@/lib/api";
import MediaLibraryPicker from "@/components/MediaLibraryPicker";

const EMPTY = { name: "", description: "", image_url: "" };
const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

export default function AdminCategories() {
  const [categories, setCategories] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/categories/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `categories_petales_de_miel_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch {
      toast.error("Export impossible, réessayez.");
    } finally {
      setExporting(false);
    }
  };

  const load = () => api.get("/admin/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  const toggleVisibility = async (c) => {
    try {
      const { data } = await api.put(`/admin/categories/${c.category_id}/visibility`, { hidden: !c.hidden });
      toast.success(data.message);
      load();
    } catch { toast.error("Mise à jour impossible"); }
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(EMPTY);
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (c) => {
    setForm({ name: c.name, description: c.description || "", image_url: c.image_url || "" });
    setEditingId(c.category_id);
    setFormOpen(true);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/categories/${editingId}`, form);
        toast.success("Catégorie mise à jour");
      } else {
        await api.post("/admin/categories", form);
        toast.success("Catégorie créée — elle apparaît sur la boutique");
      }
      setFormOpen(false);
      setEditingId(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name} » ?`)) return;
    try {
      await api.delete(`/admin/categories/${c.category_id}`);
      toast.success("Catégorie supprimée");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-categories" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-3xl font-semibold text-bark">Catégories</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="export-categories-button"
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-60"
          >
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Exporter (Excel)
          </button>
          <button
            data-testid="new-category-button"
            onClick={openCreate}
            className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95"
          >
            <Plus size={16} /> Nouvelle catégorie
          </button>
        </div>
      </div>

      {formOpen && (
        <form data-testid="category-form" onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl font-semibold text-bark">{editingId ? "Modifier la catégorie" : "Nouvelle catégorie"}</h2>
            <button type="button" data-testid="category-form-close" onClick={() => setFormOpen(false)} className="text-stone-mute hover:text-bark" aria-label="Fermer">
              <X size={18} />
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="cf-name" className="mb-1.5 block text-sm font-medium text-bark">Nom *</label>
              <input id="cf-name" data-testid="cf-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder="Miels de Noël" />
            </div>
            <div>
              <label htmlFor="cf-description" className="mb-1.5 block text-sm font-medium text-bark">Description</label>
              <input id="cf-description" data-testid="cf-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} placeholder="Une ligne qui donne envie…" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {form.image_url && (
              <img data-testid="cf-image-preview" src={resolveImage(form.image_url)} alt="Aperçu" className="h-16 w-16 rounded-xl border border-amber-900/10 object-cover" />
            )}
            <label
              data-testid="cf-upload-label"
              className={`flex cursor-pointer items-center gap-2 rounded-full border-2 border-amber-700/30 px-4 py-2.5 text-sm font-medium text-amber-900 transition-colors hover:border-amber-deep hover:bg-amber-50 ${uploading ? "opacity-60" : ""}`}
            >
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
              {uploading ? "Téléversement…" : form.image_url ? "Changer l'image" : "Téléverser une image"}
              <input data-testid="cf-upload-input" type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
            </label>
            <button
              type="button"
              data-testid="cf-library-btn"
              onClick={() => setPickerOpen(true)}
              className="flex cursor-pointer items-center gap-2 rounded-full border border-amber-900/15 px-4 py-2.5 text-sm font-medium text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep"
            >
              <Images size={15} /> Bibliothèque
            </button>
          </div>
          <MediaLibraryPicker
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={(url) => setForm((f) => ({ ...f, image_url: url }))}
            defaultTag="categories"
          />
          <button
            data-testid="cf-submit"
            type="submit"
            disabled={saving || uploading}
            className="rounded-full bg-amber-deep px-6 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : editingId ? "Enregistrer" : "Créer la catégorie"}
          </button>
        </form>
      )}

      {!categories ? (
        <p data-testid="categories-loading" className="py-16 text-center font-serif text-xl text-bark-soft">Chargement…</p>
      ) : categories.length === 0 ? (
        <p data-testid="categories-empty" className="py-16 text-center font-serif text-xl text-bark-soft">Aucune catégorie pour le moment.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {categories.map((c) => (
            <div key={c.category_id} data-testid={`category-row-${c.category_id}`} className={`flex items-center gap-4 rounded-2xl border p-5 shadow-sm ${c.hidden ? "border-dashed border-stone-300 bg-sand/40" : "border-amber-800/15 bg-white"}`}>
              {c.image_url ? (
                <img src={resolveImage(c.image_url)} alt="" className="h-16 w-16 shrink-0 rounded-xl border border-amber-900/10 object-cover" />
              ) : (
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-sand text-stone-mute">
                  <Tags size={22} />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg font-semibold text-bark">{c.name}</p>
                {c.description && <p className="truncate text-sm text-bark-soft">{c.description}</p>}
                <p data-testid={`category-count-${c.category_id}`} className="mt-1 text-xs uppercase tracking-[0.12em] text-stone-mute">
                  {c.product_count ?? 0} produit(s)
                  {c.hidden && <span data-testid={`category-hidden-badge-${c.category_id}`} className="ml-2 rounded-full bg-terra/15 px-2 py-0.5 text-[10px] font-semibold text-terra">Masquée côté client</span>}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  data-testid={`toggle-category-${c.category_id}`}
                  onClick={() => toggleVisibility(c)}
                  title={c.hidden ? "Afficher côté client" : "Masquer côté client"}
                  className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${c.hidden ? "border-terra/30 text-terra hover:bg-terra/10" : "border-sage/40 text-sage hover:bg-sage-light"}`}
                  aria-label={c.hidden ? `Afficher ${c.name}` : `Masquer ${c.name}`}
                >
                  {c.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button
                  data-testid={`edit-category-${c.category_id}`}
                  onClick={() => openEdit(c)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-800/20 text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep"
                  aria-label={`Modifier ${c.name}`}
                >
                  <Pencil size={15} />
                </button>
                <button
                  data-testid={`delete-category-${c.category_id}`}
                  onClick={() => handleDelete(c)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10"
                  aria-label={`Supprimer ${c.name}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
